import restClient from "api/restClient";
import { schemaFormValidator } from "components/Form/Form.utils";
import type { SchematizedValues } from "components/Form/components/SchemaForm/SchemaForm.types";
import { IconCollection } from "components/Icon/IconCollection";
import NotificationService from "components/Notification/Notification.service";
import RestErrorNotification from "components/Notification/RestErrorNotification";
import RecordLabel from "components/RecordLabel";
import { ResourceConfig } from "config/config";
import { ActionSignature } from "containers/Resources/components/CommonActions/Action";
import {
    DeleteListAction,
    PermanentlyDeleteListAction,
    deleteActionSignature,
    deletePermanentlyActionSignature,
    duplicateActionSignature,
    exportActionSignature,
    moveToLocationActionSignature,
} from "containers/Resources/components/CommonActions/CommonActions";
import {
    commentsTab,
    logsTab,
    logsTabKey,
    metadataTab,
    usedByTab,
    valueAndSchema,
} from "containers/Resources/screens/ResourcesSingle/constants";
import { buildSimpleColumn } from "containers/Search/components/SearchTabs/columns";
import { CustomObjectProvisioningState } from "graphql/generated/graphql";
import { isEqual } from "lodash";
import { getRelativeUrl } from "utils/service";
import resource from "./resource";
import { getLatestExecutions } from "./sharedFields";

const omitWhen = (data: any, visibleWhen: string) =>
    data?.provisioningState?.provisioning_state !== visibleWhen;

const customObjectAction = async (data: any, action: string) => {
    try {
        await restClient.post(`/custom_object/${data?.resourceData?.id}/${action}`);
    } catch (error) {
        NotificationService.error(<RestErrorNotification error={error} />);
        throw error;
    }
};

const recreateActionSignature: (visibleWhen: CustomObjectProvisioningState) => ActionSignature = (
    visibleWhen
) => ({
    key: "recreate",
    action: (data) => customObjectAction(data, "recreate"),
    label: () => "Re-create",
    icon: () => IconCollection.refresh,
    omitWhen: (data) => omitWhen(data, visibleWhen),
});
const reupdateActionSignature: (visibleWhen: string) => ActionSignature = (visibleWhen) => ({
    key: "reupdate",
    action: (data) => customObjectAction(data, "reupdate"),
    label: () => "Re-update",
    icon: () => IconCollection.refresh,
    omitWhen: (data) => omitWhen(data, visibleWhen),
});
const redeleteActionSignature: (visibleWhen: string) => ActionSignature = (visibleWhen) => ({
    key: "redelete",
    action: (data) => customObjectAction(data, "redelete"),
    label: () => "Re-delete",
    icon: () => IconCollection.deleteOutline,
    omitWhen: (data) => omitWhen(data, visibleWhen),
});

export const objectTemplateIdUrlField = "objectTemplateIdUrl";

const resourceName = "custom_object";

const config: ResourceConfig = {
    label: "Custom object",
    icon: IconCollection.customObject,
    resourceName,
    resourceClass: "resource",
    defaultActiveTab: logsTabKey,
    graphql: {
        resource: resourceName,
        resourceBody: `
            ${resource.graphql.resourceBody}
            object_template_id
            provisioning_state
            value
            object_template_id_object_template {
                name
                id
            }
        `,
        resourceBodyListing: `
            ${resource.graphql.resourceBody}
            object_template_id_object_template {
                name
                id
            }
        `,
        createVariables: `
            ${resource.graphql.createVariables(resourceName)}
            $object_template_id: String!,
            $value: SchemalessData,
        `,
        createQuery: `
            ${resource.graphql.createQuery(resourceName)}
            object_template_id: $object_template_id,
            value: $value,
        `,
        updateVariables: `
            ${resource.graphql.updateVariables(resourceName)}
            $value: SchemalessData,
        `,
        updateQuery: `
            ${resource.graphql.updateQuery(resourceName)}
            value: $value,
        `,
    },
    layout: [
        { partial: "pageHeaderForm" },
        { partial: "form" },
        {
            partial: "tabs",
            config: {
                tabs: [logsTab.tab, usedByTab.tab, metadataTab.tab, commentsTab.tab],
                content: [
                    logsTab.content,
                    usedByTab.content,
                    metadataTab.content,
                    commentsTab.content,
                ],
            },
        },
    ],
    additionalColumns: (setNewValue) => [
        {
            label: "Object template",
            key: "object_template",
            shown: true,
            sortable: false,
            dto: (value) => {
                if (value?.object_template_id_object_template) {
                    const { name, id } = value?.object_template_id_object_template;
                    const content = (
                        <RecordLabel
                            name={name}
                            link={getRelativeUrl("object_template", id)}
                            iconByType="object_template"
                        />
                    );
                    return buildSimpleColumn(
                        content,
                        setNewValue
                            ? () =>
                                  setNewValue({
                                      field: "object_template_id--object_template.id:name",
                                      value: name,
                                  })
                            : undefined
                    );
                }
            },
        },
    ],
    actions: {
        shown: [
            recreateActionSignature("CREATE_FAILED"),
            reupdateActionSignature("UPDATE_FAILED"),
            redeleteActionSignature("DELETE_FAILED"),
        ],
        hidden: [
            recreateActionSignature("READY"),
            reupdateActionSignature("READY"),
            redeleteActionSignature("DELETED"),
            duplicateActionSignature,
            exportActionSignature,
            moveToLocationActionSignature,
            deleteActionSignature,
            deletePermanentlyActionSignature,
        ],
    },
    listActions: {
        shown: [],
        hidden: [
            { key: "delete", action: DeleteListAction },
            { key: "deletePermanently", action: PermanentlyDeleteListAction },
        ],
    },
    statsSidebar: [getLatestExecutions("custom_object")],
    form: [
        {
            name: objectTemplateIdUrlField,
            component: "url",
            fieldProps: {
                label: "Object template",
                asLink: true,
                docs: {
                    text: "Choose the object template",
                    urlPath: "/docs/object-templates-custom-objects#object-templates",
                },
                relativePath: "object_template",
            },
            remappingSubmitDto: (value: { id: string; name: string }) => {
                if (value) {
                    return [
                        {
                            key: "object_template_id",
                            value: value.id,
                        },
                    ];
                } else return [];
            },
            isShownInCanvas: false,
        },
        {
            name: valueAndSchema,
            component: "schemaForm",
            fieldProps: {
                lang: "yaml",
                isReadOnly: false,
                showErrorOnTop: true,
                canEnableSaveManually: true,
            },
            remappingSubmitDto: (value: SchematizedValues) => {
                return [
                    {
                        key: "value",
                        value: value.values,
                    },
                ];
            },
            isEqual: (valueA, valueB) => {
                return isEqual(valueA, valueB);
            },
            validation: schemaFormValidator,
        },
    ],
    formDTO: {
        initialValueDTO: (data: any) => {
            const currentSchema = data?.input_schema;
            const valueAndSchemaFieldValues: SchematizedValues = {
                values: data?.value,
                schema: currentSchema,
                // While the object_template schema is still loading (websocket
                // full-state push not yet arrived), let SchemaForm show a
                // placeholder instead of flashing the raw code editor.
                isSchemaLoading: data?.isSchemaLoading,
            };
            const objectTemplateIdUrl = {
                id: data?.object_template_id,
                name: data?.object_template_id_object_template?.name,
            };
            return {
                ...data,
                [valueAndSchema]: valueAndSchemaFieldValues,
                objectTemplateIdUrl,
            };
        },
    },
};
export default config;
